import { Link } from "react-router-dom";
import type { CronJobView, RepoSummary } from "@agentdash/shared";
import { StatusDot } from "../../components/StatusDot.js";
import { Timestamp } from "../../components/Timestamp.js";
import { originToWebUrl } from "../../lib/repo-link.js";
import { formatRelative } from "../../lib/time.js";
import { CronRow } from "./CronRow.js";
import {
  composeStatusColor,
  conclusionColor,
  labeledPillStyle,
} from "./statusIcons.js";

export function RepoCard({
  repo,
  cronJobs,
}: {
  repo: RepoSummary;
  cronJobs: CronJobView[];
}) {
  return (
    <section className="panel" style={{ margin: "16px" }}>
      <div
        className="panel-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to={`/repos/${repo.slug}`} style={{ color: "var(--text)" }}>
            {repo.slug}
          </Link>
          <a
            href={originToWebUrl(repo.origin_url)}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: "var(--muted)" }}
          >
            github ↗
          </a>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", fontSize: 12 }}>
          <span style={{ color: "var(--muted)" }}>
            {cronJobs.length} job{cronJobs.length === 1 ? "" : "s"}
          </span>
          {repo.paused_count > 0 && (
            <span style={{ color: "var(--warn)" }}>
              {repo.paused_count} paused
            </span>
          )}
          {repo.wrapped_count > 0 && (
            <span style={{ color: "var(--accent)" }}>
              {repo.wrapped_count} wrapped
            </span>
          )}
          {repo.last_run_status && (
            <span title={`last run ${formatRelative(repo.last_run_at ?? "")}`}>
              <StatusDot status={repo.last_run_status} />{" "}
              <Timestamp iso={repo.last_run_at ?? ""} />
            </span>
          )}
          {repo.ci && (
            <a
              href={repo.ci.url}
              target="_blank"
              rel="noreferrer"
              title={`GitHub Actions: ${repo.ci.conclusion} · ${formatRelative(repo.ci.updated_at)}`}
              style={labeledPillStyle(conclusionColor(repo.ci.conclusion))}
            >
              CI
            </a>
          )}
          {repo.deploy && (
            <span
              title={`Dokploy: ${repo.deploy.status}`}
              style={labeledPillStyle(composeStatusColor(repo.deploy.status))}
            >
              DEPLOY
            </span>
          )}
        </div>
      </div>
      {cronJobs.length === 0 ? (
        <div style={{ padding: 16, color: "var(--muted)" }}>
          No cron jobs reported for this repo.
        </div>
      ) : (
        cronJobs.map((j) => <CronRow key={j.cron_job_id} job={j} />)
      )}
    </section>
  );
}
